// Domínio PURO do sudoku (contador por forma; gerador só 9×9) — portado de `generate-sudoku.ts` do backend
// (games-rec). Nada aqui conhece React, o documento do editor ou o blob.
//
// Por que rodar no cliente (D2): o veredito de unicidade precisa sair a cada clique;
// um `preview` debounced deixaria a grade lenta. Por que ele NÃO é a autoridade (D3):
// em divergência o 422 do servidor vence e é a mensagem dele que a tela mostra —
// isto aqui é advisory.
//
// As constantes de tamanho moram aqui, e não no `doc.ts`, porque `doc.ts` importa
// este arquivo (o `createEmpty` gera um tabuleiro) — o contrário fecharia um ciclo.

import { SUDOKU_9, type SudokuShape } from '../../../../../games/Sudoku/shape';
import type { SudokuDificuldade } from '../../../../../services/admin';

/** Lado da grade 9×9 — o único que o `generateGrid` sabe sortear. */
export const SIZE = 9;
/** Total de casas do tabuleiro. */
export const CELLS = SIZE * SIZE;

/** Grade de trabalho do solver: `size*size` casas em ordem row-major, `0` = vazia. */
export type Grid = number[];

/**
 * Quantas casas dadas cada dificuldade mira. Mesmos números do backend.
 *
 * Exportado porque o `wire.ts` usa estes mesmos alvos para INFERIR a dificuldade de
 * um tabuleiro carregado (o blob não guarda esse campo — ver wire.ts).
 */
export const GIVENS_POR_DIFICULDADE: Record<SudokuDificuldade, number> = {
  facil: 45,
  medio: 36,
  dificil: 30,
};

/** `val` cabe na casa `pos` sem repetir na linha, na coluna ou no bloco da forma? */
export function canPlace(
  grid: Grid,
  pos: number,
  val: number,
  shape: SudokuShape = SUDOKU_9,
): boolean {
  const { size, boxW, boxH } = shape;
  const row = Math.floor(pos / size);
  const col = pos % size;
  for (let i = 0; i < size; i++) {
    if (grid[row * size + i] === val) return false; // linha
    if (grid[i * size + col] === val) return false; // coluna
  }
  const boxRow = Math.floor(row / boxH) * boxH;
  const boxCol = Math.floor(col / boxW) * boxW;
  for (let i = 0; i < boxH; i++) {
    for (let j = 0; j < boxW; j++) {
      if (grid[(boxRow + i) * size + (boxCol + j)] === val) return false; // bloco
    }
  }
  return true;
}

/** Teto de nós do contador antes de desistir com `'indeterminate'` (mesmo do backend). */
export const SOLVER_NODE_LIMIT = 200_000;
export type SolutionCount = number | 'indeterminate';

function popcount(mask: number): number {
  let n = 0;
  let m = mask;
  while (m) {
    m &= m - 1;
    n++;
  }
  return n;
}

/**
 * Quantas soluções o tabuleiro admite, contando até `limit`. `2` responde a única
 * pergunta que o editor faz ("é única?") e corta o backtracking cedo (risco S4).
 * Não muta a grade recebida.
 *
 * Bitmask + MRV, o MESMO algoritmo do `sudoku-solver.ts` do backend: cada linha,
 * coluna e bloco guarda a máscara dos valores usados e a busca preenche sempre a casa
 * vazia com MENOS candidatos. Dadas em conflito (ou fora de 1..size) devolvem `0` na
 * hora — o que dispensa a antiga varredura de conflito. Passando de `nodeLimit` nós
 * sem fechar o veredito, desiste com `'indeterminate'` em vez de travar o browser.
 */
export function countSolutions(
  grid: Grid,
  shape: SudokuShape = SUDOKU_9,
  limit = 2,
  nodeLimit = SOLVER_NODE_LIMIT,
): SolutionCount {
  const { size, boxW, boxH } = shape;
  const cells = size * size;
  if (grid.length !== cells) return 0;

  const boxesPerRow = size / boxW;
  const boxOf = (r: number, c: number) => Math.floor(r / boxH) * boxesPerRow + Math.floor(c / boxW);
  const rows = new Array<number>(size).fill(0);
  const cols = new Array<number>(size).fill(0);
  const boxes = new Array<number>(size).fill(0);
  const work = [...grid];
  const empties: number[] = [];

  for (let i = 0; i < cells; i++) {
    const v = work[i];
    if (v === 0) {
      empties.push(i);
      continue;
    }
    if (!Number.isInteger(v) || v < 1 || v > size) return 0;
    const r = Math.floor(i / size);
    const c = i % size;
    const b = boxOf(r, c);
    const bit = 1 << (v - 1);
    if ((rows[r] | cols[c] | boxes[b]) & bit) return 0; // dadas em conflito
    rows[r] |= bit;
    cols[c] |= bit;
    boxes[b] |= bit;
  }

  const full = (1 << size) - 1;
  let nodes = 0;
  let found = 0;
  let aborted = false;

  const walk = (): void => {
    if (found >= limit || aborted) return;
    if (++nodes > nodeLimit) {
      aborted = true;
      return;
    }
    let best = -1;
    let bestMask = 0;
    let bestCount = size + 1;
    for (const i of empties) {
      if (work[i] !== 0) continue;
      const r = Math.floor(i / size);
      const c = i % size;
      const mask = full & ~(rows[r] | cols[c] | boxes[boxOf(r, c)]);
      const count = popcount(mask);
      if (count < bestCount) {
        best = i;
        bestMask = mask;
        bestCount = count;
        if (count === 0) break;
      }
    }
    if (best === -1) {
      found++; // nenhuma vazia sobrou = uma solução
      return;
    }
    if (bestCount === 0) return;

    const r = Math.floor(best / size);
    const c = best % size;
    const b = boxOf(r, c);
    let m = bestMask;
    while (m) {
      const bit = m & -m;
      m ^= bit;
      work[best] = 31 - Math.clz32(bit) + 1;
      rows[r] |= bit;
      cols[c] |= bit;
      boxes[b] |= bit;
      walk();
      work[best] = 0;
      rows[r] ^= bit;
      cols[c] ^= bit;
      boxes[b] ^= bit;
      if (found >= limit || aborted) return;
    }
  };

  walk();
  if (aborted && found < limit) return 'indeterminate';
  return Math.min(found, limit);
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

/** Preenche a grade inteira por backtracking randômico → solução completa. */
function fill(grid: Grid): boolean {
  const pos = grid.indexOf(0);
  if (pos === -1) return true;
  for (const n of shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9])) {
    if (canPlace(grid, pos, n)) {
      grid[pos] = n;
      if (fill(grid)) return true;
      grid[pos] = 0;
    }
  }
  return false;
}

/**
 * Sorteia uma solução completa e a máscara de casas dadas da dificuldade pedida,
 * removendo casas só enquanto a solução continuar ÚNICA (sudoku de verdade).
 *
 * Mesmo algoritmo do gerador do backend, mas devolvendo o par (solução, dadas) em
 * vez do blob: o blob é assunto do `wire.ts`.
 */
export function generateGrid(dificuldade: SudokuDificuldade): {
  solution: number[];
  givens: boolean[];
} {
  const solution: Grid = new Array(CELLS).fill(0);
  fill(solution);

  const alvo = GIVENS_POR_DIFICULDADE[dificuldade];
  const puzzle: Grid = [...solution];
  let dadas = CELLS;

  const ordem = shuffle(Array.from({ length: CELLS }, (_, i) => i));
  for (const pos of ordem) {
    if (dadas <= alvo) break;
    const backup = puzzle[pos];
    puzzle[pos] = 0;
    if (countSolutions(puzzle, SUDOKU_9, 2) !== 1) {
      puzzle[pos] = backup; // remover quebraria a unicidade → a casa fica
    } else {
      dadas--;
    }
  }

  return { solution, givens: puzzle.map((v) => v !== 0) };
}
